import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSocket } from './SocketContext';

const SCOPES = [
  'profile',
  'tax-filing',
  'documents',
  'blogs',
  'bookkeeping',
  'annual-report',
  'registered-agent',
  'usa-formation',
  'invoice',
  'meetings',
];

const createInitialVersions = () =>
  SCOPES.reduce((acc, scope) => {
    acc[scope] = 0;
    return acc;
  }, {});

const RealtimeSyncContext = createContext({
  versions: createInitialVersions(),
  portalVersions: {},
  userId: null,
});

export const RealtimeSyncProvider = ({ children }) => {
  const socket = useSocket();
  const [versions, setVersions] = useState(createInitialVersions);
  const [portalVersions, setPortalVersions] = useState({});
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const resolveUserId = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (!cancelled) setUserId(null);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (!cancelled && data?.user?._id) {
          setUserId(data.user._id);
        }
      } catch (error) {
        console.error('Realtime sync user lookup failed:', error);
      }
    };

    resolveUserId();
    const interval = setInterval(resolveUserId, 5000);
    const handleAuthChanged = () => resolveUserId();
    window.addEventListener('bookscomply-auth-changed', handleAuthChanged);
    window.addEventListener('storage', handleAuthChanged);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('bookscomply-auth-changed', handleAuthChanged);
      window.removeEventListener('storage', handleAuthChanged);
    };
  }, []);

  const bumpScopes = useCallback((scopes = []) => {
    const validScopes = scopes.filter((scope) => SCOPES.includes(scope));
    if (validScopes.length === 0) return;

    setVersions((prev) => {
      const next = { ...prev };
      validScopes.forEach((scope) => {
        next[scope] = (prev[scope] || 0) + 1;
      });
      return next;
    });
  }, []);

  const bumpPortalSlug = useCallback((slug) => {
    if (!slug) return;
    setPortalVersions((prev) => ({
      ...prev,
      [slug]: (prev[slug] || 0) + 1,
    }));
  }, []);

  useEffect(() => {
    if (!socket) return undefined;

    const handleGlobalDataUpdated = (payload) => {
      bumpScopes(payload?.scopes || []);
    };

    const handlePortalCmsUpdated = (payload) => {
      bumpPortalSlug(payload?.slug);
    };

    socket.on('globalDataUpdated', handleGlobalDataUpdated);
    socket.on('portalCmsUpdated', handlePortalCmsUpdated);

    return () => {
      socket.off('globalDataUpdated', handleGlobalDataUpdated);
      socket.off('portalCmsUpdated', handlePortalCmsUpdated);
    };
  }, [socket, bumpScopes, bumpPortalSlug]);

  useEffect(() => {
    if (!socket || !userId) return undefined;

    socket.emit('joinRoom', userId);

    const handleUserDataUpdated = (payload) => {
      bumpScopes(payload?.scopes || []);
    };

    socket.on('userDataUpdated', handleUserDataUpdated);

    return () => {
      socket.off('userDataUpdated', handleUserDataUpdated);
    };
  }, [socket, userId, bumpScopes]);

  const value = useMemo(
    () => ({ versions, portalVersions, userId, bumpScopes, bumpPortalSlug }),
    [versions, portalVersions, userId, bumpScopes, bumpPortalSlug]
  );

  return (
    <RealtimeSyncContext.Provider value={value}>
      {children}
    </RealtimeSyncContext.Provider>
  );
};

export const useRealtimeVersion = (scope) => {
  const { versions } = useContext(RealtimeSyncContext);
  return versions?.[scope] ?? 0;
};

export const useRealtimeRefresh = (scopes, callback) => {
  const scopeList = Array.isArray(scopes) ? scopes : [scopes];
  const { versions } = useContext(RealtimeSyncContext);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const refreshToken = Math.max(...scopeList.map((scope) => versions?.[scope] ?? 0));

  useEffect(() => {
    if (refreshToken === 0) return;
    callbackRef.current();
  }, [refreshToken]);
};

export const usePortalCmsVersion = (slug) => {
  const { portalVersions } = useContext(RealtimeSyncContext);
  return portalVersions?.[slug] ?? 0;
};

export const usePortalCmsRefresh = (slugs, callback) => {
  const slugList = Array.isArray(slugs) ? slugs : [slugs];
  const { portalVersions } = useContext(RealtimeSyncContext);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const refreshToken = Math.max(...slugList.map((slug) => portalVersions?.[slug] ?? 0));

  useEffect(() => {
    if (refreshToken === 0) return;
    callbackRef.current();
  }, [refreshToken]);
};
